import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./delivery-statistics.css";

/* ===== CHART SETUP (CRITICAL) ===== */
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
);

const API_BASE = process.env.REACT_APP_API_BASE;

export default function DeliveryStatistics() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [period, setPeriod] = useState("year");
  const [product, setProduct] = useState("all");
  const [color, setColor] = useState("all");
  const [size, setSize] = useState("all");

  useEffect(() => {
    fetchDeliveredOrders();
    // eslint-disable-next-line
  }, []);

  const fetchDeliveredOrders = async () => {
    const token =
      localStorage.getItem("access") || localStorage.getItem("access_token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API_BASE}/orders/delivered/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // API shape normalize
      setOrders(
        (res.data.results || res.data || []).map((order) => ({
          ...order,
          cart: order.cart || { items: order.items || [] },
        })),
      );
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.clear();
        window.location.href = "/admin/login";
      }
      // else ignore (just show empty/stateless UI)
    } finally {
      setLoading(false);
    }
  };

  /** TIME PERIOD FILTER LOGIC **/
  const isWithinPeriod = (date) => {
    if (!date) return false;
    const now = new Date();
    const d = new Date(date);
    const diff = (now - d) / (1000 * 60 * 60 * 24); // days
    if (period === "day") return diff < 1;
    if (period === "week") return diff < 7;
    if (period === "month") return diff < 31;
    if (period === "year") return diff < 366;
    return true;
  };

  /** STATISTICS **/
  const stats = useMemo(() => {
    const map = {};
    let totalQty = 0,
      totalRevenue = 0;
    orders.forEach((order) => {
      if (!isWithinPeriod(order.created_at)) return;
      (order.cart.items || []).forEach((item) => {
        if (
          (product !== "all" && item.product.name !== product) ||
          (color !== "all" && item.color !== color) ||
          (size !== "all" && item.size !== size)
        )
          return;

        const key = `${item.product.name}|${item.size}|${item.color}`;
        const revenue =
          (item.quantity || 0) * (Number(item.product.price) || 0);

        if (!map[key]) {
          map[key] = {
            product: item.product.name,
            size: item.size || "-",
            color: item.color || "-",
            quantity: 0,
            revenue: 0,
          };
        }
        map[key].quantity += item.quantity || 0;
        map[key].revenue += revenue;

        totalQty += item.quantity || 0;
        totalRevenue += revenue;
      });
    });
    return {
      rows: Object.values(map),
      totalQty,
      totalRevenue,
    };
  }, [orders, period, product, color, size]);

  /** FILTER OPTIONS FOR SELECT INPUTS **/
  // Always unique by item existence
  const products = useMemo(
    () => [
      ...new Set(
        orders.flatMap((order) =>
          (order.cart.items || []).map((i) => i.product.name),
        ),
      ),
    ],
    [orders],
  );
  const colors = useMemo(
    () => [
      ...new Set(
        orders.flatMap((order) =>
          (order.cart.items || []).map((i) => i.color).filter(Boolean),
        ),
      ),
    ],
    [orders],
  );
  const sizes = useMemo(
    () => [
      ...new Set(
        orders.flatMap((order) =>
          (order.cart.items || []).map((i) => i.size).filter(Boolean),
        ),
      ),
    ],
    [orders],
  );

  /** CHART DATA **/
  const labels = stats.rows.map((r) => `${r.product} (${r.size}/${r.color})`);
  const gradientFor = (ctx, colorStart, colorEnd) => {
    const chart = ctx.chart;
    const { ctx: c, chartArea } = chart;
    if (!chartArea) return colorStart;
    const grad = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    grad.addColorStop(0, colorStart);
    grad.addColorStop(1, colorEnd);
    return grad;
  };

  const barChartData = {
    labels,
    datasets: [
      {
        label: "Delivered Quantity",
        data: stats.rows.map((r) => r.quantity),
        backgroundColor: (context) =>
          gradientFor(context, "rgba(37,99,235,0.85)", "rgba(37,99,235,0.06)"),
        borderRadius: 8,
        borderSkipped: false,
        maxBarThickness: 48,
      },
    ],
  };

  const lineChartData = {
    labels,
    datasets: [
      {
        label: "Revenue (ETB)",
        data: stats.rows.map((r) => Number(r.revenue.toFixed(2))),
        borderColor: "#10B981",
        backgroundColor: (context) =>
          gradientFor(
            context,
            "rgba(16,185,129,0.18)",
            "rgba(16,185,129,0.02)",
          ),
        tension: 0.35,
        fill: true,
        pointRadius: 3,
        borderWidth: 2.5,
      },
    ],
  };

  if (loading) return <p className="loading">Loading statistics...</p>;

  return (
    <div className="stats-page">
      <h2>Delivery Statistics</h2>

      {/* FILTERS */}
      <div className="filters">
        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="year">This Year</option>
        </select>
        <select value={product} onChange={(e) => setProduct(e.target.value)}>
          <option value="all">All Products</option>
          {products.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <select value={color} onChange={(e) => setColor(e.target.value)}>
          <option value="all">All Colors</option>
          {colors.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select value={size} onChange={(e) => setSize(e.target.value)}>
          <option value="all">All Sizes</option>
          {sizes.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {/* METRICS */}
      <div className="metrics">
        <div className="metric">
          <span>Total Delivered Quantity</span>
          <strong>{stats.totalQty}</strong>
        </div>
        <div className="metric">
          <span>Total Revenue</span>
          <strong>ETB {stats.totalRevenue.toFixed(2)}</strong>
        </div>
      </div>

      {/* TABLE */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Size</th>
              <th>Color</th>
              <th>Delivered Qty</th>
              <th>Total Revenue</th>
            </tr>
          </thead>
          <tbody>
            {stats.rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  No data found for filters
                </td>
              </tr>
            ) : (
              stats.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.product}</td>
                  <td>{r.size}</td>
                  <td>{r.color}</td>
                  <td>{r.quantity}</td>
                  <td>ETB {r.revenue.toFixed(2)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* GRAPHS */}
      <div className="charts-section">
        <h3 className="charts-title">Visual Delivery Analysis</h3>
        {stats.rows.length > 0 ? (
          <div className="charts-grid">
            <div className="chart-card" style={{ minHeight: 320 }}>
              <Bar data={barChartData} />
            </div>
            <div className="chart-card" style={{ minHeight: 320 }}>
              <Line data={lineChartData} />
            </div>
          </div>
        ) : (
          <p style={{ marginTop: 20 }}>No data for selected filters</p>
        )}
      </div>
    </div>
  );
}
